import { getApiConfig } from "../config";
import { proxyFetch } from "./proxyFetch";

export interface MarginCostDayRow {
  date: string;
  marginUsed: number;
  dailyInterest: number;
  /** Running month total through this day. */
  total: number;
  annualRatePct: number | null;
  filled: boolean;
  updatedAt: string | null;
}

export interface MarginCostMonthSummary {
  ok?: boolean;
  monthKey: string;
  days: MarginCostDayRow[];
  accrued: number;
  predictedMonthCost: number;
  currentDailyInterest: number;
  currentMarginUsed: number;
  currentAnnualRatePct: number | null;
  daysInMonth: number;
  daysElapsed: number;
  daysRemaining: number;
  sampleCount: number;
  updatedAt: string | null;
}

const MARGIN_COST_HISTORY_PATH = "/margin-cost-history";

function historyUrl(monthKey?: string): string {
  const { baseUrl } = getApiConfig();
  const base = baseUrl ? baseUrl.replace(/\/$/, "") : "";
  const qs = monthKey ? `?month=${encodeURIComponent(monthKey)}` : "";
  return `${base}${MARGIN_COST_HISTORY_PATH}${qs}`;
}

export async function fetchMarginCostMonthSummary(
  monthKey?: string,
): Promise<MarginCostMonthSummary | null> {
  try {
    const res = await proxyFetch(historyUrl(monthKey), {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as MarginCostMonthSummary;
    if (!data || typeof data !== "object" || !Array.isArray(data.days)) return null;
    return {
      monthKey: typeof data.monthKey === "string" ? data.monthKey : monthKey ?? "",
      days: data.days,
      accrued: Number(data.accrued) || 0,
      predictedMonthCost: Number(data.predictedMonthCost) || 0,
      currentDailyInterest: Number(data.currentDailyInterest) || 0,
      currentMarginUsed: Number(data.currentMarginUsed) || 0,
      currentAnnualRatePct: (() => {
        const rate = Number(data.currentAnnualRatePct);
        return Number.isFinite(rate) ? rate : null;
      })(),
      daysInMonth: Number(data.daysInMonth) || 0,
      daysElapsed: Number(data.daysElapsed) || 0,
      daysRemaining: Number(data.daysRemaining) || 0,
      sampleCount: Number(data.sampleCount) || 0,
      updatedAt: typeof data.updatedAt === "string" ? data.updatedAt : null,
    };
  } catch {
    return null;
  }
}
